import { useState } from "react";
import { Button } from "../ui/button";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Broom, Eye, CheckCircle } from "@phosphor-icons/react";

/**
 * One-shot DB migration — rewrites every truck's `cuisine_tags` with the
 * canonicalised version returned by `_clean_cuisine_tags` on the server.
 * Strips the legacy `"default"` slug, hashtag blobs (`"#a, #b, #c"`),
 * obvious overlong free-text, and case-insensitively dedupes.
 *
 * Flow: dry-run preview → review the per-truck diff → commit if it
 * looks right. Idempotent — running again after a clean run is a no-op.
 */
export default function CleanupCuisineTagsCard() {
  const [busy, setBusy] = useState(null); // "dry" | "apply" | null
  const [result, setResult] = useState(null);
  const [committed, setCommitted] = useState(false);

  const run = async (mode) => {
    setBusy(mode);
    setCommitted(mode === "apply");
    try {
      const dryRun = mode === "dry";
      const r = await api.post(
        `/admin/cleanup-cuisine-tags?dry_run=${dryRun ? "true" : "false"}`
      );
      setResult(r.data);
      toast.success(
        dryRun
          ? `Dry run complete · ${r.data.would_change} of ${r.data.scanned} trucks would change.`
          : `Migration applied · ${r.data.changed} of ${r.data.scanned} trucks updated.`
      );
    } catch (e) {
      toast.error(formatApiError(e, "Cleanup failed"));
      setResult(null);
    } finally {
      setBusy(null);
    }
  };

  const headlineCount = result
    ? result.would_change ?? result.changed ?? 0
    : null;
  const sample = result?.sample_diffs || [];

  return (
    <section className="mb-6" data-testid="cleanup-cuisine-tags-card">
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 mb-1">
          <Broom size={18} weight="duotone" className="text-primary" />
          <h2 className="font-display font-semibold text-lg tracking-tight">
            Clean up cuisine_tags
          </h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
          One-shot DB migration. Strips the legacy <code>default</code> slug,
          hashtag blobs, overlong free-text, and case-insensitive duplicates
          (e.g. <code>bbq</code> + <code>BBQ</code> collapse to one). Always
          dry-run first so you can eyeball the diff before committing.
          Idempotent · safe to re-run any number of times.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => run("dry")}
            disabled={busy !== null}
            data-testid="cleanup-cuisine-dry-run"
            className="gap-2"
          >
            <Eye size={14} weight="bold" />
            {busy === "dry" ? "Running…" : "Dry run"}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              if (
                !window.confirm(
                  "Permanently rewrite cuisine_tags on every affected truck? Idempotent — safe but visible to all admins. Run a dry-run first if you haven't."
                )
              )
                return;
              run("apply");
            }}
            disabled={busy !== null}
            data-testid="cleanup-cuisine-apply"
            className="gap-2"
          >
            <CheckCircle size={14} weight="bold" />
            {busy === "apply" ? "Applying…" : "Apply for real"}
          </Button>
        </div>

        {result && (
          <div
            className="mt-5 rounded-xl border border-border bg-background/50 p-4"
            data-testid="cleanup-cuisine-result"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {committed ? "Committed" : "Dry-run preview"}
                </div>
                <div className="text-xl font-display font-bold tabular-nums">
                  {headlineCount} of {result.scanned} trucks{" "}
                  {committed ? "updated" : "would change"}
                </div>
              </div>
              {result.trucks_left_with_no_tags > 0 && (
                <div className="text-xs text-amber-700 dark:text-amber-400 bg-amber-100/60 dark:bg-amber-900/30 px-2 py-1 rounded">
                  {result.trucks_left_with_no_tags} truck
                  {result.trucks_left_with_no_tags === 1 ? "" : "s"} would be
                  left with no tags after cleanup
                </div>
              )}
            </div>

            {sample.length === 0 ? (
              <p className="text-sm text-muted-foreground mt-3">
                No changes needed — DB is already clean.
              </p>
            ) : (
              <div className="mt-3">
                <div className="text-xs font-semibold text-muted-foreground mb-2">
                  Sample diffs (showing up to 50)
                </div>
                <div className="divide-y divide-border rounded-lg border border-border overflow-hidden">
                  {sample.map((d) => (
                    <div
                      key={d.truck_id}
                      className="px-3 py-2 text-xs"
                      data-testid={`cleanup-cuisine-diff-${d.truck_id}`}
                    >
                      <div className="font-semibold truncate">
                        {d.truck_name || d.truck_id}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        <span className="text-muted-foreground">before:</span>
                        {d.before.map((t, i) => (
                          <span
                            key={`b-${i}`}
                            className="line-through text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/40 px-1.5 rounded"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        <span className="text-muted-foreground">after:</span>
                        {d.after.length === 0 ? (
                          <span className="text-amber-700 dark:text-amber-400">
                            (none)
                          </span>
                        ) : (
                          d.after.map((t, i) => (
                            <span
                              key={`a-${i}`}
                              className="text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/40 px-1.5 rounded"
                            >
                              {t}
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {result.diffs_count > sample.length && (
                  <p className="text-[11px] text-muted-foreground mt-2">
                    {result.diffs_count - sample.length} more diffs not shown.
                    Apply will affect all {result.diffs_count} trucks.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
